import { batchProcess } from "../../../../../../api/recordApi";
import type {
  RowData,
  SaveRecordRequest,
  SetupBaseRequestInfo,
} from "../../../../../../types";
import { str } from "./fieldUtils";

// ── Public Helpers ───────────────────────────────────────────────────────────

/**
 * Resolve unique identity for an inventory building record.
 * Prefers dms_building_id, falls back to id, then to fallback index.
 */
export const getInventoryBuildingIdentity = (
  record: RowData,
  fallbackIndex: number,
): string => {
  const identity = record.dms_building_id ?? record.id;
  const identityStr = str(identity);
  return identityStr === "" ? String(fallbackIndex) : identityStr;
};

/**
 * Normalize records to sequential priorities (1, 2, 3, ...).
 */
export const normalizeInventoryBuildingPriorities = (
  records: RowData[],
): RowData[] => {
  return records.map((record, index) => ({
    ...record,
    priority: index + 1,
  }));
};

// ── Persistence Logic ────────────────────────────────────────────────────────

/**
 * Persist batch of normalized records via batchProcess API.
 * The backend handles DB rollback automatically on failure.
 * UI only needs to check the top-level resultCode.
 * Returns false on non-200 resultCode so the caller can display a failure toast.
 */
const persistWithBatchProcess = async ({
  records,
  setupBaseRequestInfo,
}: {
  records: RowData[];
  setupBaseRequestInfo: SetupBaseRequestInfo;
}): Promise<boolean> => {
  const request: SaveRecordRequest = {
    ...setupBaseRequestInfo,
    record: records,
  };

  const response = await batchProcess(request);
  return Boolean(response?.resultCode?.includes("200"));
};

// ── Main Export ──────────────────────────────────────────────────────────────

/**
 * Persist reordered inventory building records with normalized sequential priorities.
 * Returns success/failure status along with normalized records for rollback if needed.
 */
export async function persistInventoryBuildingPriorities({
  records,
  setupBaseRequestInfo,
}: {
  records: RowData[];
  setupBaseRequestInfo: SetupBaseRequestInfo;
}): Promise<{ isSuccess: boolean; records: RowData[] }> {
  const normalizedRecords = normalizeInventoryBuildingPriorities(records);
  const isSuccess = await persistWithBatchProcess({
    records: normalizedRecords,
    setupBaseRequestInfo,
  });
  return { isSuccess, records: normalizedRecords };
}
